import {
  keepPreviousData,
  type QueryClient,
  useQuery
} from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  organizationsAccessListOptions,
  organizationsAccessListQueryKey,
  organizationsAccessRetrieveQueryKey,
  usersRetrieveQueryKey
} from 'src/api/main'
import type { OrganizationsAccessListData } from 'src/api/main'

import type { UserStatusFilter, WorkspaceUser } from '../types'

/**
 * The API already accepts `active` / `inactive` / `blocked`, but the generated
 * client still carries the older `online` / `offline` union until the next
 * schema pull — hence the one cast in `buildQuery`.
 */
type ApiStatusFilter = NonNullable<
  OrganizationsAccessListData['query']
>['status']

type Query = {
  page?: number
  per_page?: number
  search?: string
  ordering?: string
  team?: number[]
  status?: UserStatusFilter
  /** `YYYY-MM-DD`; backed by the DRF `DateFromToRangeFilter` on `date_created`. */
  joined_date_after?: string
  joined_date_before?: string
}

type Args = {
  workspaceId: number
  enabled?: boolean
  query?: Query
}

const buildQuery = (workspaceId: number, query: Query = {}) => {
  const {
    search,
    ordering,
    page,
    per_page,
    team,
    status,
    joined_date_after,
    joined_date_before
  } = query

  return {
    organization: workspaceId,
    ...(page && page > 1 ? { page } : {}),
    ...(per_page ? { per_page } : {}),
    ...(search ? { search } : {}),
    ...(ordering ? { ordering } : {}),
    ...(team?.length ? { team } : {}),
    ...(status ? { status: status as unknown as ApiStatusFilter } : {}),
    ...(joined_date_after ? { joined_date_after } : {}),
    ...(joined_date_before ? { joined_date_before } : {})
  }
}

/** Wrapper around organizationsAccessList scoped to a workspace. */
export const useWorkspaceUsersList = ({
  workspaceId,
  enabled = true,
  query: queryArgs
}: Args) => {
  const query = useQuery({
    ...organizationsAccessListOptions({
      query: buildQuery(workspaceId, queryArgs)
    }),
    enabled: enabled && Number.isFinite(workspaceId),
    placeholderData: keepPreviousData
  })

  const users = useMemo<WorkspaceUser[]>(
    () => query.data?.results ?? [],
    [query.data]
  )

  return { ...query, users, total: query.data?.count ?? users.length }
}

/**
 * Invalidation key that matches every paginated/filtered variation for a
 * workspace. TanStack Query does partial-deep-equal matching on object keys,
 * so this acts as a prefix over `{ organization, ...anything }`.
 */
export const workspaceUsersListQueryKey = (workspaceId: number) =>
  organizationsAccessListQueryKey({
    query: { organization: workspaceId }
  })

export const workspaceUsersRosterQueryKey = (workspaceId: number) =>
  organizationsAccessListQueryKey({
    query: { organization: workspaceId, per_page: 500 }
  })

type InvalidateWorkspaceUserArgs = {
  workspaceId: number
  organizationAccessId: number
  userId: number
}

type InvalidateWorkspaceUserEditArgs = {
  workspaceId: number
  userId: number
}

export const invalidateWorkspaceUserEditQueries = (
  queryClient: QueryClient,
  { workspaceId, userId }: InvalidateWorkspaceUserEditArgs
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: workspaceUsersRosterQueryKey(workspaceId),
      refetchType: 'all'
    }),
    queryClient.invalidateQueries({
      queryKey: usersRetrieveQueryKey({ path: { id: userId } }),
      refetchType: 'all'
    })
  ])

export const invalidateWorkspaceUserQueries = (
  queryClient: QueryClient,
  { workspaceId, organizationAccessId, userId }: InvalidateWorkspaceUserArgs
) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: workspaceUsersListQueryKey(workspaceId),
      refetchType: 'all'
    }),
    queryClient.invalidateQueries({
      queryKey: organizationsAccessRetrieveQueryKey({
        path: { id: organizationAccessId }
      }),
      refetchType: 'all'
    }),
    queryClient.invalidateQueries({
      queryKey: usersRetrieveQueryKey({ path: { id: userId } }),
      refetchType: 'all'
    })
  ])
